import { useGraphQLQuery } from '@betterment/graphql';
import {
  Box,
  Button,
  FormikField as Field,
  H1,
  Input,
  Label,
  ListRow,
  Text,
  ToggleGroup,
} from '@betterment/samba-react';
import { SambaAutoLink, WorkflowLayout } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { Form, Formik, useFormikContext } from 'formik';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { PurposeCreationIntent } from '../../../../graphql/schema';
import Footer from '../../../../Shared/components-v2/footer/Footer';
import {
  NameAndImagePurposeCreationOptionsDocument,
  NameAndImagePurposeCreationOptionsQuery,
} from '../../graphql/query.purposeCreationOption.generated';
import { nameAndImage } from '../../sharedSchemas';
import { useMajorPurchaseStep } from '../FlowConfig';

type FormValues = Yup.InferType<typeof nameAndImage>;
type PurposeCreationOptions = NameAndImagePurposeCreationOptionsQuery['purposeCreationOptions'];
type PurposeCreationOption = PurposeCreationOptions[0];

interface AdviceTypeToggleGroupProps {
  purposeCreationOptions: PurposeCreationOption[];
}

const AdviceTypeToggleGroup: React.FC<AdviceTypeToggleGroupProps> = ({ purposeCreationOptions }) => {
  const { setFieldValue } = useFormikContext();

  const handleToggleChange = (value: string) => {
    const option = purposeCreationOptions.find(opt => opt.id === value) || purposeCreationOptions[0];
    void setFieldValue('name', option.defaultName);
  };

  return (
    <ToggleGroup
      spacing="default"
      type="single"
      css={{ backgroundColor: 'background.surface', mb: 'space.400' }}
      onValueChange={handleToggleChange}
    >
      {purposeCreationOptions.map(option => (
        <ToggleGroup.Item
          value={option.id}
          key={option.id}
          css={{
            backgroundColor: 'background.surface',
            borderColor: 'border.weak',
            borderStyle: 'solid',
            borderWidth: 'thin',
            borderRadius: 'round',
          }}
        >
          <Text css={{ px: 'space.100' }}>{option.defaultName}</Text>
        </ToggleGroup.Item>
      ))}
    </ToggleGroup>
  );
};

export const MajorPurchaseNameAndImageStep: React.FC = () => {
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { data } = useGraphQLQuery(NameAndImagePurposeCreationOptionsDocument, {
    variables: { intent: PurposeCreationIntent.MajorPurchase },
  });

  const { commit } = useMajorPurchaseStep('nameAndImage');

  if (!data) {
    return '';
  }

  const { purposeCreationOptions } = data;

  const handleSubmit = (values: FormValues) => {
    commit({
      ...values,
      purpose_sub_type: 'build_wealth',
    });
    void navigate(`/add_purpose/major_purchase/${flowId}/target_details`);
  };

  return (
    <WorkflowLayout pageTitle={null}>
      <WorkflowLayout.Header
        workflowTitle="Choose a name and image for this goal"
        onClose={() => (returnTo ? navigate(returnTo) : navigate('/'))}
      />
      <WorkflowLayout.Content>
        <H1 css={{ mb: 'space.400' }}>Tell us about your goal.</H1>
        <Text css={{ mb: 'space.200' }}>What are you saving for?</Text>
        <Formik
          onSubmit={handleSubmit}
          initialValues={{ name: '', imageType: '' }}
          validationSchema={nameAndImage}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={false}
        >
          <Form>
            <AdviceTypeToggleGroup purposeCreationOptions={purposeCreationOptions} />
            <Box css={{ display: 'grid', gap: 'space.200', maxWidth: '400px' }}>
              <Field name="name">
                <Field.Label>Goal name</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.Description>You can change this name anytime.</Field.Description>
                <Field.ErrorMessage />
              </Field>

              <Label>Goal image</Label>
              <SambaAutoLink to="/image_selection">
                <ListRow css={{ p: 0 }}>
                  <ListRow.LeadingAsset css={{ w: 'size.450', h: 'size.450' }}>
                    <ThemedImage
                      themedAsset={purposeCreationOptions[0].purposeAsset.icon}
                      alt={purposeCreationOptions[0].defaultName}
                    />
                  </ListRow.LeadingAsset>
                  <ListRow.LeadingContent>
                    <Text css={{ verticalAlign: 'center' }}>Change or upload</Text>
                  </ListRow.LeadingContent>
                </ListRow>
              </SambaAutoLink>
              <Button type="submit" variant="solidPrimary" css={{ width: 'fit-content' }}>
                Continue
              </Button>
            </Box>
          </Form>
        </Formik>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
